import React from 'react';
import { Card, FormSelect } from '@/components/UI';
import ExpandableDataTable from '@/components/UI/ExpandableDataTable';
import ParameterPreferenceControls from '../ParameterPreferenceControls';
import type { ColumnDef, Row } from '@/components/UI/dataTableConfig';
import type { useParameterPreferences } from '@/hooks/useParameterPreferences';
import type { ParameterColumnPreset, ParameterDiscoveryMetric, ParameterTableRow, ParameterTrailingWindowFilter } from './types';

type ParameterPreferencesApi = ReturnType<typeof useParameterPreferences>;

interface ParameterAnalysisTableCardProps {
    archivedCount: number;
    filteredTableData: ParameterTableRow[];
    onScopedReset: () => void;
    optimizationMode: ParameterDiscoveryMetric;
    parameterColumnPreset: ParameterColumnPreset;
    pinnedCount: number;
    renderSubComponent: (props: { row: Row<ParameterTableRow> }) => React.ReactElement;
    setParameterColumnPreset: (preset: ParameterColumnPreset) => void;
    setShowArchived: ParameterPreferencesApi['setShowArchived'];
    setTrailingWindowFilter: (filter: ParameterTrailingWindowFilter) => void;
    setViewMode: ParameterPreferencesApi['setViewMode'];
    showArchived: boolean;
    trailingWindowCounts: Record<ParameterTrailingWindowFilter, number>;
    trailingWindowFilter: ParameterTrailingWindowFilter;
    viewMode: ParameterPreferencesApi['viewMode'];
    visibleParameterColumns: ColumnDef<ParameterTableRow>[];
}

const ParameterAnalysisTableCard = ({
    archivedCount,
    filteredTableData,
    onScopedReset,
    optimizationMode,
    parameterColumnPreset,
    pinnedCount,
    renderSubComponent,
    setParameterColumnPreset,
    setShowArchived,
    setTrailingWindowFilter,
    setViewMode,
    showArchived,
    trailingWindowCounts,
    trailingWindowFilter,
    viewMode,
    visibleParameterColumns
}: ParameterAnalysisTableCardProps) => {
    return (
        <Card className="bg-zinc-900 p-4">
            <div className="mb-4 space-y-3">
                <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                    <div>
                        <h3 className="text-lg font-semibold text-white">Parameter Analysis & Impact Ranking</h3>
                        <span className="mt-1 block text-sm text-zinc-500">
                            Click on a parameter to expand and view detailed value breakdowns
                        </span>
                    </div>
                    <div className="flex flex-col gap-3 xl:items-end">
                        <ParameterPreferenceControls
                            archivedCount={archivedCount}
                            pinnedCount={pinnedCount}
                            showArchived={showArchived}
                            viewMode={viewMode}
                            onReset={pinnedCount > 0 || archivedCount > 0 ? onScopedReset : undefined}
                            onShowArchivedChange={setShowArchived}
                            onViewModeChange={setViewMode}
                        />
                        <div className="flex flex-wrap gap-3 xl:justify-end">
                            <FormSelect
                                noMargin
                                className="min-w-[180px]"
                                fullWidth={false}
                                label="Feature Window"
                                options={[
                                    { value: 'all', label: `All (${trailingWindowCounts.all})` },
                                    { value: 'trailing', label: `Trailing only (${trailingWindowCounts.trailing})` },
                                    { value: 'base', label: `Base/current only (${trailingWindowCounts.base})` }
                                ]}
                                size="sm"
                                value={trailingWindowFilter}
                                onChange={(e) => setTrailingWindowFilter(e.target.value as ParameterTrailingWindowFilter)}
                            />
                            <FormSelect
                                noMargin
                                className="min-w-[180px]"
                                fullWidth={false}
                                label="Columns"
                                options={[
                                    { value: 'core', label: 'Core' },
                                    { value: 'statistical', label: 'Statistical' },
                                    { value: 'all', label: 'All' }
                                ]}
                                size="sm"
                                value={parameterColumnPreset}
                                onChange={(e) => setParameterColumnPreset(e.target.value as ParameterColumnPreset)}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <ExpandableDataTable
                allowOverflow
                search
                sort
                striped
                columns={visibleParameterColumns}
                data={filteredTableData}
                initialSorting={[
                    {
                        id: optimizationMode === 'options' ? 'optionImpactScore' : 'impactScore',
                        desc: true
                    }
                ]}
                key={`table-${optimizationMode}`}
                pagination={{ enabled: filteredTableData.length > 15, pageSize: 15 }}
                renderSubComponent={renderSubComponent}
            />
        </Card>
    );
};

export default ParameterAnalysisTableCard;
